import { FormControl, Alert, InputGroup } from 'react-bootstrap';
import { StackColumn } from './StackColumn';
import { GlobalStackButtons } from './GlobalStackButtons';
import { CreateStackModal } from './CreateStackModal';
import { LoginModal } from './LoginModal';
import { React, useState, useEffect, Fragment } from "react";
import styled from 'styled-components';
import { Beforeunload } from 'react-beforeunload';
import { clone, shuffle } from './arrays';
import { recalculateTasks, adjustTask } from './tasks';
import { normalizeDocument, buildUploadPayload, createEmptyStack } from './stackDocument';
import { getTokenCookie, setTokenCookie, clearTokenCookie } from './authCookie';
import { completionFromTask } from './velocity';
import { VelocityChart } from './VelocityChart';

const CenteredAlert = styled(Alert)`
  position: fixed; 
  top: 50%;
  left: 50%;
`;

const BlockedFilterContainer = styled.div`
 display:flex;
 flex-direction: row;
 min-width: 100px;
`;

const FilterAndBlockedContainer = styled.div`
  display:flex;
  flex-direction: row;
  margin:5px;
  align-items: center;
  gap: 8px;
`;

const Board = styled.div`
  display: flex;
  flex-direction: row;
  margin: 5px;
  justify-content: flex-start;
  align-items: flex-end;
  overflow-x: auto;
`;

const asInt = (count) => {
  if (count == null) {
    return 0;
  }
  return parseInt(count);
}

function Stack() {
  const baseUrl = "https://thelaboflieven.info/sess/";
  const [filter, setFilter] = useState("");
  const [stacks, setStacks] = useState([createEmptyStack()]);
  const [loggedIn, setLoggedIn] = useState(false);
  const [loginToken, setLoginToken] = useState("");
  const [showSaved, setShowSaved] = useState(false);
  const [showItCrashed, setShowCrashed] = useState(false);
  const [crashReason, setCrashReason] = useState("");
  const [changedSinceSave, setChangedSinceSave] = useState(false);
  const [timespent, setTimespent] = useState("0");
  const [blockedFilter, setBlockedFilter] = useState(false);

  const setLogin = (token) => {
    setTokenCookie(token);
    setLoginToken(token);
    download(token);
  }

  useEffect(() => {
    const savedToken = getTokenCookie();
    if (savedToken) {
      setLoginToken(savedToken);
      download(savedToken);
    }
    // Auto-login once from cookie on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    clearTokenCookie();
    window.location.reload();
  }

  const recalculateStackTimeleft = (stack, filterValue) => {
    let timeleft = stack.timeleft;
    recalculateTasks(stack.tasks, (value) => { timeleft = value; }, filterValue);
    return { ...stack, timeleft };
  }

  const commitStacks = (nextStacks) => {
    const withTime = nextStacks.map((s) => recalculateStackTimeleft(s, filter));
    setStacks(withTime);
    setChangedSinceSave(true);
  }

  const updateStack = (stackId, updater) => {
    commitStacks(stacks.map((s) => {
      if (s.id !== stackId) {
        return s;
      }
      return updater({ ...s, tasks: clone(s.tasks) });
    }));
  }

  const moveLastToTop = (stackId) => {
    updateStack(stackId, (stack) => {
      if (stack.tasks.length === 0) {
        return stack;
      }
      const tasks = clone(stack.tasks);
      const last = tasks.pop();
      tasks.unshift(last);
      return { ...stack, tasks };
    });
  }

  const editTask = (stackId, task) => {
    updateStack(stackId, (stack) => ({
      ...stack,
      tasks: adjustTask(stack.tasks, task),
    }));
  }

  const moveCurrentToBottom = (stackId, taskId) => {
    updateStack(stackId, (stack) => ({
      ...stack,
      tasks: [
        ...stack.tasks.filter((t) => t.taskId !== taskId),
        ...stack.tasks.filter((t) => t.taskId === taskId),
      ],
    }));
  }

  const shuffleTasks = (stackId) => {
    updateStack(stackId, (stack) => ({
      ...stack,
      tasks: shuffle(clone(stack.tasks)),
    }));
  }

  const sortByCompletionTime = (stackId) => {
    updateStack(stackId, (stack) => ({
      ...stack,
      tasks: clone(stack.tasks).sort((a, b) => asInt(b.seriesCount) - asInt(a.seriesCount)),
    }));
  }

  const changeFilter = (event) => {
    const nextFilter = event.target.value;
    setFilter(nextFilter);
    setStacks(stacks.map((s) => recalculateStackTimeleft(s, nextFilter)));
  }

  const addTaskToStack = (stackId, task) => {
    updateStack(stackId, (stack) => ({
      ...stack,
      tasks: [...stack.tasks, task],
    }));
  }

  const completeTask = (stackId, taskId) => {
    const stack = stacks.find((s) => s.id === stackId);
    if (!stack) {
      return;
    }
    const completed = stack.tasks.filter((t) => t.taskId === taskId);
    if (completed.length > 0) {
      // seriesCount may be 0, "0", or empty from the number input
      const hours = !completed[0].seriesCount || completed[0].seriesCount === "0"
        ? 1
        : parseInt(completed[0].seriesCount, 10);
      setTimespent(timespent + hours);
    }
    updateStack(stackId, (s) => ({
      ...s,
      tasks: s.tasks.filter((t) => t.taskId !== taskId),
      completions: completed.length > 0
        ? [...(s.completions || []), completionFromTask(completed[0])]
        : (s.completions || []),
    }));
  }

  const splitTaskToStack = (stackId, taskId, tasksToAdd) => {
    updateStack(stackId, (stack) => ({
      ...stack,
      tasks: [...stack.tasks.filter((t) => t.taskId !== taskId), ...tasksToAdd],
    }));
  }

  const changeBlockedFilter = (event) => {
    setBlockedFilter(event.target.checked);
  }

  const changeStackTitle = (stackId, title) => {
    if (stacks[0] && stacks[0].id === stackId) {
      document.title = title;
    }
    updateStack(stackId, (stack) => ({ ...stack, title }));
  }

  const createStack = (name) => {
    commitStacks([...stacks, createEmptyStack(name)]);
  }

  const upload = () => {
    const Http = new XMLHttpRequest();
    const url = baseUrl + '/upload.php';
    const tasksObject = buildUploadPayload(stacks);
    Http.open("POST", url);
    Http.setRequestHeader('token', loginToken);
    Http.send(JSON.stringify(tasksObject));

    Http.onreadystatechange = (e) => {
      if (e.target.readyState === 4) {
        if (e.target.status === 200) {
          setShowSaved(true);
          window.setTimeout(function () { setShowSaved(false); }, 3000);
          setChangedSinceSave(false);
        }
        if (e.target.status > 200) {
          setCrashReason(e.target.responseText);
          setShowCrashed(true);
          window.setTimeout(function () { setShowCrashed(false); }, 3000);
        }
      }
    }
  }

  const download = (token) => {
    const Http = new XMLHttpRequest();
    const url = baseUrl + '/download.php';
    Http.open("GET", url);
    Http.setRequestHeader('token', token);
    Http.send();

    Http.onreadystatechange = (e) => {
      if (e.target.readyState === 4 && e.target.status === 200) {
        let tasksObjects = JSON.parse(Http.responseText.trim());
        const { stacks: loadedStacks } = normalizeDocument(tasksObjects);
        const withTime = loadedStacks.map((s) => recalculateStackTimeleft(s, filter));
        setStacks(withTime);
        const first = withTime[0];
        const localTimeleft = first ? first.timeleft : "";
        setTimespent(localTimeleft == null ? 0 : parseInt(localTimeleft, 10));
        document.title = first ? first.title : "";
        setLoggedIn(true);
        setChangedSinceSave(false);
      }
    }
  }

  const refresh = () => {
    download(loginToken);
  }

  return <Fragment>
    {!loggedIn && <LoginModal register={setLogin}></LoginModal>}
    {loggedIn &&
      <Fragment>
        {changedSinceSave && <Beforeunload onBeforeunload={() => 'You’ll lose your data!'} />}
        <VelocityChart stacks={stacks} filter={filter} />
        <FilterAndBlockedContainer>
          <FormControl style={{ textAlign: "center" }} data-cy="stack-filter"
            required
            placeholder="filter"
            aria-label="filter"
            value={filter} onChange={changeFilter}></FormControl>
          <BlockedFilterContainer>
            <InputGroup>
              <InputGroup.Text id="blocked">Blocked</InputGroup.Text>
            </InputGroup>
            <FormControl style={{ textAlign: "center" }}
              type="checkbox"
              placeholder="blocked"
              aria-label="blocked"
              checked={blockedFilter} data-cy="blocked-filter" onChange={changeBlockedFilter}></FormControl>
          </BlockedFilterContainer>
          <CreateStackModal onCreate={createStack} />
        </FilterAndBlockedContainer>
        <Board data-cy="stacks-board">
          {
            stacks.map((stack) =>
              <StackColumn
                key={stack.id}
                stack={stack}
                filter={filter}
                blockedFilter={blockedFilter}
                onTitleChange={(title) => changeStackTitle(stack.id, title)}
                moveLastToTop={() => moveLastToTop(stack.id)}
                shuffleTasks={() => shuffleTasks(stack.id)}
                sortByCompletionTime={() => sortByCompletionTime(stack.id)}
                addTaskToStack={(task) => addTaskToStack(stack.id, task)}
                editTask={(task) => editTask(stack.id, task)}
                completeTask={(taskId) => completeTask(stack.id, taskId)}
                moveCurrentToBottom={(taskId) => moveCurrentToBottom(stack.id, taskId)}
                splitTaskToStack={(taskId, tasksToAdd) => splitTaskToStack(stack.id, taskId, tasksToAdd)}
              />
            )
          }
        </Board>
        <GlobalStackButtons
          changed={changedSinceSave}
          timespent={timespent}
          upload={upload}
          refresh={refresh}
          logout={logout}
        />
      </Fragment>
    }
    {showSaved && <CenteredAlert data-cy={"alert-save-success"} variant={"success"}>Saved</CenteredAlert>}
    {showItCrashed && <CenteredAlert data-cy={"error-panel"} variant={"danger"}>Failed! {crashReason}</CenteredAlert>}

  </Fragment>;
}

export { Stack };
